// nuevo header.
import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

const getBrowserLang = () => {
  const lang = navigator.language || navigator.userLanguage || 'es';
  return lang.toLowerCase().startsWith('en') ? 'en' : 'es';
};

const DEMO_URL = 'https://emojisurveys.pro/surveyRes/nD9YdUknboKU5zrM'; // ← NUEVO

const Header = ({ onNavigate }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleMenuItemClick = (page) => {
    onNavigate(page);
    handleMenuClose();
  };

  // ← NUEVO: abre el demo en nueva pestaña
  const handleDemo = () => {
    window.open(DEMO_URL, '_blank', 'noopener,noreferrer');
  };

  const colors = {
    primary: '#667eea',
    secondary: '#763ea2',
    backgroundGradient: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)',
    backgroundShadow: 'rgba(102, 126, 234, 0.3)',
    buttonTransparent: 'rgba(255, 255, 255, 0.1)',
    buttonTransparentHover: 'rgba(255, 255, 255, 0.2)',
    buttonOutline: 'rgba(255, 255, 255, 0.2)',
    buttonSolid: 'white',
    textPrimary: 'white',
    textSecondary: '#667eea',
    white: 'white',
    shadowLight: '0 2px 8px rgba(0, 0, 0, 0.1)',
    shadowMedium: '0 4px 12px rgba(0, 0, 0, 0.15)',
    shadowHeavy: '0 6px 16px rgba(255, 255, 255, 0.4)',
    shadowWhite: '0 4px 12px rgba(255, 255, 255, 0.3)',
    // ← NUEVO: colores para el botón demo
    demoBackground: 'rgba(255, 220, 50, 0.15)',
    demoBackgroundHover: 'rgba(255, 220, 50, 0.28)',
    demoBorder: 'rgba(255, 220, 50, 0.55)',
    demoText: '#FFE066',
  };

  const logoStyle = {
    width: 32,
    height: 32,
    backgroundColor: 'transparent',
    borderRadius: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0
  };

  const navButtonStyle = {
    backgroundColor: colors.buttonTransparent,
    border: 'none',
    fontSize: '13px',
    fontWeight: theme.typography.fontWeightMedium,
    color: colors.textPrimary,
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 1.5,
    transition: 'all 0.2s',
    backdropFilter: 'blur(10px)',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: colors.buttonTransparentHover,
      transform: 'translateY(-1px)'
    }
  };

  // ← NUEVO: estilo del botón demo
  const demoButtonStyle = {
    backgroundColor: colors.demoBackground,
    border: `1.5px solid ${colors.demoBorder}`,
    fontSize: '13px',
    fontWeight: theme.typography.fontWeightSemiBold || 600,
    color: colors.demoText,
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 1.5,
    transition: 'all 0.2s',
    backdropFilter: 'blur(10px)',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: colors.demoBackgroundHover,
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(255, 220, 50, 0.25)'
    }
  };

  const loginButtonStyle = {
    padding: theme.spacing(0.875, 1.75),
    backgroundColor: colors.buttonOutline,
    color: colors.textPrimary,
    border: `2px solid ${colors.white}`,
    borderRadius: 1.5,
    fontSize: '13px',
    fontWeight: theme.typography.fontWeightMedium,
    backdropFilter: 'blur(10px)',
    transition: 'all 0.2s',
    transform: 'translateY(0)',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: colors.white,
      color: colors.primary,
      transform: 'translateY(-1px)',
      boxShadow: colors.shadowMedium
    }
  };

  const registerButtonStyle = {
    padding: theme.spacing(0.875, 1.75),
    backgroundColor: colors.buttonSolid,
    color: colors.primary,
    border: 'none',
    borderRadius: 1.5,
    fontSize: '13px',
    fontWeight: theme.typography.fontWeightMedium,
    boxShadow: colors.shadowWhite,
    transition: 'all 0.2s',
    transform: 'translateY(0)',
    minWidth: 'auto',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: colors.shadowHeavy
    }
  };

  const translations = {
    es: {
      platformName: "EmojiSurveys",
      home: "🏠 Home",
      contact: "📬 Contacto",
      demo: "🚀 Ver Ejemplo",        // ← NUEVO
      login: "Iniciar Sesión",
      register: "✨ Registrarse",
      menuLogin: "🔐 Iniciar Sesión",
      menuRegister: "✨ Registrarse",
      menuContact: "📬 Contacto",
      menuDemo: "🚀 Ver Ejemplo",    // ← NUEVO
    },
    en: {
      platformName: "EmojiSurveys",
      home: "🏠 Home",
      contact: "📬 Contact",
      demo: "🚀 Try Demo",           // ← NUEVO
      login: "Log In",
      register: "✨ Sign Up",
      menuLogin: "🔐 Log In",
      menuRegister: "✨ Sign Up",
      menuContact: "📬 Contact",
      menuDemo: "🚀 Try Demo",       // ← NUEVO
    }
  };

  const t = translations[getBrowserLang()];

  return (
    <AppBar
      position="sticky"
      sx={{
        background: colors.backgroundGradient,
        backdropFilter: 'blur(10px)',
        boxShadow: `0 4px 20px ${colors.backgroundShadow}`,
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 1
      }}
    >
      <Toolbar
        sx={{
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          padding: isMobile ? theme.spacing(1, 1.5) : theme.spacing(1.5, 2.5),
          minHeight: isMobile ? 56 : 64,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {isMobile && (
          <IconButton
            onClick={handleMenuOpen}
            sx={{ color: colors.textPrimary, marginRight: 1.5 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1.5 : 3, flex: 1 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', flexShrink: 0 }}
            onClick={() => onNavigate('home')}
          >
            <Box sx={logoStyle}>
              <img
                src="/logop.png"
                alt="EmojiSurveys Logo"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: isMobile ? '18px' : '22px',
                fontWeight: theme.typography.fontWeightBold,
                color: colors.textPrimary,
                margin: 0,
                letterSpacing: '-0.5px'
              }}
            >
              {t.platformName}
            </Typography>
          </Box>

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <Button onClick={() => onNavigate('home')} sx={navButtonStyle} disableRipple>
                {t.home}
              </Button>
              <Button onClick={() => onNavigate('contacto')} sx={navButtonStyle} disableRipple>
                {t.contact}
              </Button>
              {/* ↓ NUEVO botón Ver Ejemplo */}
              <Button onClick={handleDemo} sx={demoButtonStyle} disableRipple>
                {t.demo}
              </Button>
            </Box>
          )}
        </Box>

        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 1.5, marginLeft: 'auto', flexShrink: 0 }}>
            <Button onClick={() => onNavigate('login')} sx={loginButtonStyle} variant="outlined" disableRipple>
              {t.login}
            </Button>
            <Button onClick={() => onNavigate('register')} sx={registerButtonStyle} variant="contained" disableRipple>
              {t.register}
            </Button>
          </Box>
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          sx={{
            '& .MuiPaper-root': {
              backgroundColor: 'white',
              borderRadius: 2,
              boxShadow: colors.shadowMedium,
              marginTop: 1,
              minWidth: 180
            }
          }}
        >
          {/* ↓ NUEVO item Ver Ejemplo en menú móvil */}
          <MenuItem
            onClick={handleDemo}
            sx={{
              padding: theme.spacing(1.5, 2),
              fontSize: '14px',
              fontWeight: theme.typography.fontWeightMedium,
              color: '#b8860b',
              '&:hover': { backgroundColor: 'rgba(255, 220, 50, 0.12)' }
            }}
          >
            {t.menuDemo}
          </MenuItem>
          <MenuItem
            onClick={() => handleMenuItemClick('contacto')}
            sx={{
              padding: theme.spacing(1.5, 2),
              fontSize: '14px',
              fontWeight: theme.typography.fontWeightMedium,
              color: colors.primary,
              '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.08)' }
            }}
          >
            {t.menuContact}
          </MenuItem>
          <MenuItem
            onClick={() => handleMenuItemClick('login')}
            sx={{
              padding: theme.spacing(1.5, 2),
              fontSize: '14px',
              fontWeight: theme.typography.fontWeightMedium,
              color: colors.primary,
              '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.08)' }
            }}
          >
            {t.menuLogin}
          </MenuItem>
          <MenuItem
            onClick={() => handleMenuItemClick('register')}
            sx={{
              padding: theme.spacing(1.5, 2),
              fontSize: '14px',
              fontWeight: theme.typography.fontWeightMedium,
              color: colors.primary,
              '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.08)' }
            }}
          >
            {t.menuRegister}
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;